import React, { type FC, useState, useMemo } from "react";
import NiceModal, {
  type NiceModalHocProps,
  useModal
} from "@ebay/nice-modal-react";
import KC_Modal_2 from "../../../designsystem/KC_Modal/KC_Modal_2";
import KC_Heading3 from "../../../designsystem/typography/KC-Heading/KC_Heading3";
import { tt } from "../../../utils/i18n/i18n";
import KC_Button, {
  KC_ButtonSize
} from "../../../designsystem/KC_Button/KC_Button";
import { useManagedAccountsSelector } from "../../../redux/actions/user";
import type ManagedAccount from "@tootsweet/model/entities/ManagedAccount";
import "./styles.scss";
import { ControlledManagedAccountDropdown } from "../../ManagedAccountDropdown/ControlledManagedAccountDropdown";

export interface AccountCreationStep1Choice {
  isSameLegalEntity: boolean;
  selectedAccount?: ManagedAccount;
}

const AccountCreationStep1Modal: FC<NiceModalHocProps> = NiceModal.create(
  () => {
    const modal = useModal();
    const managedAccounts = useManagedAccountsSelector();
    const [selectedOption, setSelectedOption] = useState<"none" | "yes" | "no">(
      "none"
    );
    const [selectedAccount, setSelectedAccount] = useState<
      ManagedAccount | undefined
    >(managedAccounts?.[0]);
    const [selectedAccountIndex, setSelectedAccountIndex] = useState<
      number | undefined
    >(undefined);

    const handleClose = () => {
      modal.resolve(null);
      void modal.hide();
    };

    const handleSubmit = () => {
      const result: AccountCreationStep1Choice = {
        isSameLegalEntity: selectedOption === "yes",
        selectedAccount: selectedOption === "yes" ? selectedAccount : undefined
      };
      modal.resolve(result);
      void modal.hide();
    };

    const isSubmitDisabled = useMemo(() => {
      if (selectedOption === "yes") {
        return !selectedAccount;
      }
      return false;
    }, [selectedOption, selectedAccount]);

    return (
      <KC_Modal_2
        id="account-creation-step-1-modal"
        isOpen={modal.visible}
        onClose={handleClose}
        allowOverflow={true}
        header={
          <div className="mt-2">
            <KC_Heading3
              text={tt("new_venue_step1_title")}
              primary={false}
              secondary={false}
            />
          </div>
        }
      >
        <div className="p-4 d-flex flex-column st-gap">
          <div>
            <label className="d-flex align-items-center cursor-pointer">
              <input
                type="radio"
                name="legal-entity"
                value="no"
                checked={selectedOption === "no"}
                onChange={(e) => setSelectedOption(e.target.value as "no")}
                className="mr-2"
              />
              <span>{tt("new_venue_same_legal_entity_no")}</span>
            </label>
          </div>
          <div className="mt-4">
            <label className="d-flex align-items-center cursor-pointer">
              <input
                type="radio"
                name="legal-entity"
                value="yes"
                checked={selectedOption === "yes"}
                onChange={(e) => setSelectedOption(e.target.value as "yes")}
                className="mr-2"
              />
              <span>{tt("new_venue_same_legal_entity_yes")}</span>
            </label>
            {selectedOption === "yes" && !!managedAccounts?.length && (
              <div className="ml-4 mt-2">
                <ControlledManagedAccountDropdown
                  accounts={managedAccounts}
                  selectedAccount={selectedAccount}
                  selectedAccountIndex={selectedAccountIndex}
                  onAccountChange={(account, index) => {
                    setSelectedAccount(account);
                    setSelectedAccountIndex(index);
                  }}
                  placeholder={tt("new_venue_select_account")}
                />
              </div>
            )}
            {selectedOption === "yes" && !managedAccounts?.length && (
              <div className="ml-4 text-muted small">
                {tt("new_venue_no_accounts_available")}
              </div>
            )}
          </div>
          <div className="d-flex justify-content-end mt-3">
            <KC_Button
              id="account-creation-step1-cancel"
              text={tt("cancel")}
              isInverted={true}
              size={KC_ButtonSize.Medium}
              onClick={handleClose}
              className="mr-2"
            />
            <KC_Button
              id="account-creation-step1-submit"
              text={tt("continue")}
              size={KC_ButtonSize.Medium}
              onClick={handleSubmit}
              disabled={isSubmitDisabled || selectedOption === "none"}
            />
          </div>
        </div>
      </KC_Modal_2>
    );
  }
);

export default AccountCreationStep1Modal;
